/**
 * MathCoachTypingIndicator
 *
 * Context-aware typing indicator for Math Coach sessions (US-005).
 * Renders a different UI depending on the current polling phase:
 *
 *   image-delay   → Static text "Your tutor is reviewing the question..." (no animation)
 *   text-delay    → Static text "Your tutor is thinking..." (no animation)
 *   reconnecting  → Static text "Reconnecting…" (no animation)
 *   polling       → Three animated bounce dots (existing design system pattern)
 *   idle          → null (renders nothing)
 *
 * This component has zero business logic — it is a pure rendering component that
 * maps a pollingPhase prop to the correct visual output.
 *
 * AC coverage:
 *   AC-005.1 - Static text during 30s image-delay (no animation)
 *   AC-005.2 - Static text during 10s text-delay (no animation)
 *   AC-005.3 - Animated dots during active polling phase
 *   AC-005.4 - Returns null (idle) when tutor message arrives
 */

import type { MathCoachPollingPhase } from '@/hooks/useSessionMessages';

export interface MathCoachTypingIndicatorProps {
  /** Current polling phase from useSessionMessages */
  pollingPhase: MathCoachPollingPhase;
}

/**
 * MathCoachTypingIndicator — maps pollingPhase to a visual indicator.
 * Only used in Math Coach sessions (/math-coach).
 * ConversationPanel (English tutor) is completely unchanged.
 */
export function MathCoachTypingIndicator({ pollingPhase }: MathCoachTypingIndicatorProps) {
  // idle phase: render nothing
  if (pollingPhase === 'idle') {
    return null;
  }

  // Determine inner content based on phase
  let innerContent: React.ReactNode;

  switch (pollingPhase) {
    case 'image-delay':
      innerContent = (
        <p className="text-sm text-otter-earth-600 italic">
          Your tutor is reviewing the question...
        </p>
      );
      break;

    case 'text-delay':
      innerContent = (
        <p className="text-sm text-otter-earth-600 italic">
          Your tutor is thinking...
        </p>
      );
      break;

    case 'reconnecting':
      innerContent = (
        <p className="text-sm text-otter-earth-600 italic">
          Reconnecting…
        </p>
      );
      break;

    case 'polling':
    default:
      // Three animated dots — copied verbatim from MathCoachPage.tsx (pre-US-005)
      innerContent = (
        <div className="flex items-center gap-1">
          <span
            className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <span
            className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <span
            className="w-2 h-2 bg-pebbles-400 rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      );
      break;
  }

  return (
    <div
      className="flex items-start gap-2 mb-4"
      role="status"
      aria-live="polite"
      data-testid="math-coach-typing-indicator"
      data-phase={pollingPhase}
    >
      <div className="bg-otter-sand rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
        {innerContent}
      </div>
    </div>
  );
}
